/**
 * CompanyCard - Main component for displaying company information
 * Professional, modern design with improved UX
 */
import { CompanyCardProps } from '../../types/company'
import { CompanyInfo } from './CompanyInfo'
import { CompanyMetadata } from './CompanyMetadata'
import { Card } from '../ui/Card'
import { Building2, AlertCircle, CheckCircle2, Loader2, ExternalLink } from 'lucide-react'

export function CompanyCard({
  entity,
  localData,
  showEditWebsite = false,
  onEditWebsite,
  loading = false,
  error = null,
  className = '',
  compact = false,
  showMetadata = true,
  excludeMetadataFields = [],
}: CompanyCardProps) {
  // Loading state with professional spinner
  if (loading) {
    return (
      <Card className={`p-8 ${className}`}>
        <div className="flex flex-col items-center justify-center py-16">
          <Loader2 className="w-8 h-8 text-primary animate-spin mb-4" />
          <p className="text-tf-text-muted font-medium">Loading company information...</p>
          <p className="text-sm text-tf-text-muted mt-2">This may take a few moments</p>
        </div>
      </Card>
    )
  }

  // Error state
  if (error) {
    return (
      <Card className={`p-8 ${className}`}>
        <div className="bg-tf-feedback-error-bg border-l-4 border-tf-danger rounded-lg p-6">
          <div className="flex items-start">
            <AlertCircle className="w-6 h-6 text-tf-feedback-error-text mt-0.5 mr-4 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-tf-feedback-error-text mb-1">Error Loading Data</h3>
              <p className="text-sm text-tf-feedback-error-text mb-4">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center px-4 py-2 bg-tf-danger text-white text-sm font-medium rounded-md hover:bg-tf-danger-hover transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </Card>
    )
  }

  // No entity state
  if (!entity) {
    return (
      <Card className={`p-8 ${className}`}>
        <div className="bg-tf-feedback-info-bg border border-tf-border rounded-xl p-8 text-center">
          <div className="flex flex-col items-center">
            <div className="w-16 h-16 bg-tf-bg-hover rounded-full flex items-center justify-center mb-4">
              <Building2 className="w-8 h-8 text-tf-feedback-info-text" />
            </div>
            <h3 className="text-xl font-semibold text-tf-text mb-2">
              Processing Your Company Information
            </h3>
            <p className="text-tf-text-muted max-w-md">
              We're analyzing and gathering information about your company. Please check back later
              to see the complete results.
            </p>
            <div className="mt-6 flex items-center gap-2 text-sm text-tf-text-muted">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Processing in background...</span>
            </div>
          </div>
        </div>
      </Card>
    )
  }

  // Entity with error state
  if (entity.error) {
    return (
      <Card className={`p-8 ${className}`}>
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <Building2 className="w-6 h-6 text-tf-text-sub" />
            <h2 className="text-2xl font-bold text-tf-text">Company Information</h2>
          </div>
          <p className="text-sm text-tf-text-muted">Company entity data</p>
        </div>
        <div className="bg-amber-50 border-l-4 border-amber-400 rounded-lg p-6">
          <div className="flex items-start">
            <AlertCircle className="w-6 h-6 text-amber-600 mt-0.5 mr-4 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="text-base font-semibold text-amber-900 mb-1">
                Unable to Load Complete Information
              </h3>
              <p className="text-sm text-amber-800 mb-3">{entity.error}</p>
              {entity.id && (
                <div className="mt-3 pt-3 border-t border-amber-200">
                  <p className="text-xs font-medium text-amber-700 mb-1">Entity ID:</p>
                  <code className="text-xs bg-amber-100 px-2 py-1 rounded text-amber-900 font-mono break-all">
                    {entity.id}
                  </code>
                </div>
              )}
            </div>
          </div>
        </div>
      </Card>
    )
  }

  // Entity with pending status
  if (entity.status && entity.status !== 'completed') {
    return (
      <Card className={`p-8 ${className}`}>
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <Building2 className="w-6 h-6 text-tf-text-sub" />
            <h2 className="text-2xl font-bold text-tf-text">Company Information</h2>
          </div>
        </div>
        <div className="bg-tf-feedback-info-bg border-l-4 border-blue-500 rounded-lg p-6">
          <div className="flex items-start">
            <Loader2 className="w-6 h-6 text-tf-feedback-info-text mt-0.5 mr-4 flex-shrink-0 animate-spin" />
            <div className="flex-1">
              <h3 className="text-base font-semibold text-tf-feedback-info-text mb-1">
                Entity Creation in Progress
              </h3>
              <p className="text-sm text-tf-feedback-info-text mb-2">
                Status: <span className="font-semibold capitalize">{entity.status}</span>
              </p>
              <p className="text-xs text-tf-feedback-info-text">
                Information will be available once processing is complete.
              </p>
            </div>
          </div>
        </div>
      </Card>
    )
  }

  // Normal entity display - Professional layout
  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header Section */}
      <Card className="p-6 bg-tf-bg-card">
        <div className="flex items-center gap-4 mb-5">
          <div className="w-12 h-12 bg-gradient-to-br from-primary to-primary-dark rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
            <Building2 className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-tf-text">Company Information</h2>
            <p className="text-sm text-tf-text-muted">
              AI-powered company insights
              {entity.updated_at && (
                <span className="text-tf-text-sub">
                  {' · '}Updated {new Date(entity.updated_at).toLocaleDateString('en-US')}
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Basic Information */}
        <CompanyInfo
          entity={entity}
          showEditWebsite={showEditWebsite}
          onEditWebsite={onEditWebsite}
          compact={compact}
        />
      </Card>

      {/* Metadata Sections */}
      {showMetadata && entity.metadata && Object.keys(entity.metadata).length > 0 && (
        <CompanyMetadata metadata={entity.metadata} compact={compact} excludeFields={excludeMetadataFields} />
      )}

      {/* Local Data Section */}
      {localData && !compact && (
        <Card className="p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-tf-feedback-info-bg rounded-lg flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-tf-feedback-info-text" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-tf-text">Additional Information</h3>
              <p className="text-sm text-tf-text-muted">Local profile data</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {localData.businessName && !entity.name && (
              <div className="p-4 bg-tf-bg-muted rounded-lg border border-tf-border">
                <label className="text-xs font-semibold text-tf-text-muted uppercase tracking-wide mb-1 block">
                  Business Name
                </label>
                <p className="text-base font-medium text-tf-text">{localData.businessName}</p>
              </div>
            )}
            {localData.website && !entity.domain && (
              <div className="p-4 bg-tf-bg-muted rounded-lg border border-tf-border">
                <label className="text-xs font-semibold text-tf-text-muted uppercase tracking-wide mb-1 block">
                  Website
                </label>
                <a
                  href={localData.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-base font-medium text-primary hover:text-primary-dark inline-flex items-center gap-1.5 group"
                >
                  {localData.website}
                  <ExternalLink className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </a>
              </div>
            )}
            {localData.primaryMarket && (
              <div className="p-4 bg-tf-bg-muted rounded-lg border border-tf-border">
                <label className="text-xs font-semibold text-tf-text-muted uppercase tracking-wide mb-1 block">
                  Primary Market
                </label>
                <p className="text-base font-medium text-tf-text">{localData.primaryMarket}</p>
              </div>
            )}
            {localData.brandSummary && (
              <div className="p-4 bg-tf-bg-muted rounded-lg border border-tf-border md:col-span-2">
                <label className="text-xs font-semibold text-tf-text-muted uppercase tracking-wide mb-1 block">
                  Brand Summary
                </label>
                <p className="text-base text-tf-text-sub leading-relaxed">{localData.brandSummary}</p>
              </div>
            )}
            {localData.contactEmail && (
              <div className="p-4 bg-tf-bg-muted rounded-lg border border-tf-border">
                <label className="text-xs font-semibold text-tf-text-muted uppercase tracking-wide mb-1 block">
                  Contact Email
                </label>
                <a
                  href={`mailto:${localData.contactEmail}`}
                  className="text-base font-medium text-primary hover:text-primary-dark"
                >
                  {localData.contactEmail}
                </a>
              </div>
            )}
            {localData.contactPhone && (
              <div className="p-4 bg-tf-bg-muted rounded-lg border border-tf-border">
                <label className="text-xs font-semibold text-tf-text-muted uppercase tracking-wide mb-1 block">
                  Contact Phone
                </label>
                <a
                  href={`tel:${localData.contactPhone}`}
                  className="text-base font-medium text-primary hover:text-primary-dark"
                >
                  {localData.contactPhone}
                </a>
              </div>
            )}
            {localData.productOffer && (
              <div className="p-4 bg-tf-bg-muted rounded-lg border border-tf-border md:col-span-2">
                <label className="text-xs font-semibold text-tf-text-muted uppercase tracking-wide mb-1 block">
                  Product Offer
                </label>
                <p className="text-base text-tf-text-sub leading-relaxed">{localData.productOffer}</p>
              </div>
            )}
            {localData.targetRegions && localData.targetRegions.length > 0 && (
              <div className="p-4 bg-tf-bg-muted rounded-lg border border-tf-border md:col-span-2">
                <label className="text-xs font-semibold text-tf-text-muted uppercase tracking-wide mb-2 block">
                  Target Regions
                </label>
                <div className="flex flex-wrap gap-2">
                  {localData.targetRegions.map((region, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium bg-tf-feedback-info-bg text-tf-feedback-info-text border border-tf-border"
                    >
                      {region}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  )
}
